import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../services/check_login';
import { FiEye, FiEdit, FiTrash2, FiMessageSquare } from 'react-icons/fi';
import './VersionHistory.css';

const VersionHistory = ({ creativeId, creativeType, highlightVersionId, onClose, onRestore }) => {
  const { user: currentUser } = useAuth();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedVersions, setExpandedVersions] = useState(new Set());
  const [selectedLanguages, setSelectedLanguages] = useState({}); // { versionNumber: languageIndex }
  const [comments, setComments] = useState({}); // { versionId: [comments] }
  const [newComment, setNewComment] = useState({}); // { versionId: commentText }
  const [submittingComment, setSubmittingComment] = useState({});
  const [editingComment, setEditingComment] = useState(null); // { versionId, commentId, text }
  const [deletingComment, setDeletingComment] = useState(null);
  const [expandedComments, setExpandedComments] = useState(new Set()); // Track which versions have comments expanded
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [versionToRestore, setVersionToRestore] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [notification, setNotification] = useState({ message: '', type: '' }); // type: 'success' or 'error'
  const [allUsers, setAllUsers] = useState([]); // For @ mentions
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [currentTextarea, setCurrentTextarea] = useState(null);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [languageCodeMap, setLanguageCodeMap] = useState({});

  // Permission check
  const permissions = currentUser?.role?.permissions || {};
  const canViewHistory = permissions.canViewHistory || currentUser?.isAdmin;
  const canRestoreHistory = permissions.canRestoreHistory || currentUser?.isAdmin;

  useEffect(() => {
    fetchVersions();
    fetchUsers();
    fetchLanguageCodes();
  }, [creativeId]);

  const fetchLanguageCodes = async () => {
    try {
      const response = await axios.get('/language/view');
      const map = {};
      (response.data || []).forEach(language => {
        map[language.languageName] = language.code;
      });
      setLanguageCodeMap(map);
    } catch (error) {
      console.error('Error fetching language codes:', error);
    }
  };

  const getLangLabel = (name) => languageCodeMap[name]?.toUpperCase() || name;

  // Scroll to and expand highlighted version
  useEffect(() => {
    if (highlightVersionId && versions.length > 0) {
      const version = versions.find(v => v._id === highlightVersionId);
      if (version) {
        // Expand comments for this version
        setExpandedComments(new Set([highlightVersionId]));
        
        // Scroll to the version after a short delay to ensure it's rendered
        setTimeout(() => {
          const element = document.getElementById(`version-${highlightVersionId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Add highlight effect
            element.classList.add('highlighted-version');
            setTimeout(() => {
              element.classList.remove('highlighted-version');
            }, 3000);
          }
        }, 300);
      }
    }
  }, [highlightVersionId, versions]);

  // Close mention dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showMentionDropdown && !e.target.closest('.mention-dropdown') && !e.target.closest('.comment-input')) {
        setShowMentionDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMentionDropdown]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/library/users-for-mentions', {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter out current user and already filtered Admin/admin on backend
      const filteredUsers = (response.data.users || []).filter(
        user => user.username !== currentUser?.username
      );
      setAllUsers(filteredUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchVersions = async () => {
    try {
      setLoading(true);
      
      // Fetch version history
      const versionsResponse = await axios.get(`/creative/versions/${creativeId}/versions`);
      
      setVersions(versionsResponse.data);
      
      // Fetch comments for all versions
      await fetchAllComments(versionsResponse.data);
      
      // Auto-expand first version (Original)
      if (versionsResponse.data.length > 0) {
        setExpandedVersions(new Set([versionsResponse.data[0].versionNumber]));
      }
    } catch (error) {
      console.error('Error fetching versions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllComments = async (versionsList) => {
    try {
      const commentsData = {};
      for (const version of versionsList) {
        const response = await axios.get(`/creative/versions/${creativeId}/versions/${version._id}/comments`);
        commentsData[version._id] = response.data || [];
      }
      setComments(commentsData);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleAddComment = async (versionId) => {
    const commentText = newComment[versionId]?.trim();
    if (!commentText) return;

    try {
      setSubmittingComment({ ...submittingComment, [versionId]: true });
      const mentionedUsers = extractMentionedUsers(commentText);
      
      const response = await axios.post(`/creative/versions/${creativeId}/versions/${versionId}/comments`, {
        text: commentText,
        userId: currentUser?.id || currentUser?.username,
        userName: `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || currentUser?.username || 'User',
        mentionedUsers
      });
      
      setComments({
        ...comments,
        [versionId]: [...(comments[versionId] || []), response.data]
      });
      
      setNewComment({ ...newComment, [versionId]: '' });
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment');
    } finally {
      setSubmittingComment({ ...submittingComment, [versionId]: false });
    }
  };

  const handleEditComment = async (versionId, commentId) => {
    const editedText = editingComment?.text?.trim();
    if (!editedText) return;

    try {
      const mentionedUsers = extractMentionedUsers(editedText);
      
      const response = await axios.put(`/creative/versions/${creativeId}/versions/${versionId}/comments/${commentId}`, {
        text: editedText,
        userId: currentUser?.username,
        mentionedUsers
      });
      
      setComments({
        ...comments,
        [versionId]: (comments[versionId] || []).map(c => 
          c._id === commentId ? response.data : c
        )
      });
      
      setEditingComment(null);
    } catch (error) {
      console.error('Error editing comment:', error);
      alert('Failed to edit comment');
    }
  };

  const handleDeleteComment = async (versionId, commentId) => {
    try {
      setDeletingComment(commentId);
      await axios.delete(`/creative/versions/${creativeId}/versions/${versionId}/comments/${commentId}`, {
        data: { userId: currentUser?.username }
      });
      
      setComments({
        ...comments,
        [versionId]: (comments[versionId] || []).filter(c => c._id !== commentId)
      });
    } catch (error) {
      console.error('Error deleting comment:', error);
    } finally {
      setDeletingComment(null);
    }
  };

  const toggleVersion = (versionNumber) => {
    setExpandedVersions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(versionNumber)) {
        newSet.delete(versionNumber);
      } else {
        newSet.add(versionNumber);
      }
      return newSet;
    });
  };

  const toggleComments = (versionId) => {
    setExpandedComments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(versionId)) {
        newSet.delete(versionId);
      } else {
        newSet.add(versionId);
      }
      return newSet;
    });
  };

  const handleRestore = (versionNumber) => {
    setVersionToRestore(versionNumber);
    setShowConfirmModal(true);
  };

  const confirmRestore = async () => {
    setShowConfirmModal(false);
    try {
      const fullName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim();
      const userName = fullName || currentUser?.username || 'Unknown User';
      await axios.post(`/creative/versions/${creativeId}/restore/${versionToRestore}`, {
        restoredBy: userName
      });
      const versionLabel = versionToRestore === 1 ? 'Original' : `version ${versionToRestore - 1}`;
      setNotification({ message: `${versionLabel} has been successfully restored!`, type: 'success' });
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 3000);
      // Refresh version history to show the restored version
      fetchVersions();
    } catch (error) {
      console.error('Error restoring version:', error);
      setNotification({ message: 'Failed to restore version. Please try again.', type: 'error' });
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 3000);
    }
    setVersionToRestore(null);
  };

  const cancelRestore = () => {
    setShowConfirmModal(false);
    setVersionToRestore(null);
  };

  // Handle @ mention functionality
  const handleTextareaChange = (e, versionId, isEdit = false) => {
    const textarea = e.target;
    const text = textarea.value;
    const cursorPos = textarea.selectionStart;

    // Update the comment text
    if (isEdit) {
      setEditingComment({ ...editingComment, text });
    } else {
      setNewComment({ ...newComment, [versionId]: text });
    }

    // Check for @ symbol before cursor
    const textBeforeCursor = text.substring(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');

    if (atIndex !== -1 && atIndex === cursorPos - 1) {
      // Just typed @
      setMentionSearch('');
      setShowMentionDropdown(true);
      setCurrentTextarea(versionId + (isEdit ? '_edit' : ''));
      setCursorPosition(cursorPos);
      
      // Calculate dropdown position
      const rect = textarea.getBoundingClientRect();
      const lineHeight = 20;
      setMentionPosition({
        top: rect.top + lineHeight,
        left: rect.left + 10
      });
    } else if (atIndex !== -1 && cursorPos > atIndex) {
      // Typing after @
      const searchText = textBeforeCursor.substring(atIndex + 1);
      if (searchText.includes(' ') || searchText.includes('\n')) {
        // Space or newline typed, close dropdown
        setShowMentionDropdown(false);
      } else {
        setMentionSearch(searchText);
        setShowMentionDropdown(true);
        setCurrentTextarea(versionId + (isEdit ? '_edit' : ''));
        setCursorPosition(cursorPos);
        
        const rect = textarea.getBoundingClientRect();
        const lineHeight = 20;
        setMentionPosition({
          top: rect.top + lineHeight,
          left: rect.left + 10
        });
      }
    } else {
      setShowMentionDropdown(false);
    }
  };

  const handleMentionSelect = (user) => {
    const [versionId, type] = currentTextarea.split('_');
    const isEdit = type === 'edit';
    const text = isEdit ? editingComment.text : (newComment[versionId] || '');
    
    // Find @ position
    const beforeCursor = text.substring(0, cursorPosition);
    const atIndex = beforeCursor.lastIndexOf('@');
    
    // Replace @search with @username
    const newText = text.substring(0, atIndex) + `@${user.username} ` + text.substring(cursorPosition);
    
    if (isEdit) {
      setEditingComment({ ...editingComment, text: newText });
    } else {
      setNewComment({ ...newComment, [versionId]: newText });
    }
    
    setShowMentionDropdown(false);
    setMentionSearch('');
  };

  const getFilteredUsers = () => {
    if (!mentionSearch) return allUsers;
    return allUsers.filter(user => 
      user.username.toLowerCase().includes(mentionSearch.toLowerCase()) ||
      (user.firstName && user.firstName.toLowerCase().includes(mentionSearch.toLowerCase())) ||
      (user.lastName && user.lastName.toLowerCase().includes(mentionSearch.toLowerCase())) ||
      (user.role && user.role.name && user.role.name.toLowerCase().includes(mentionSearch.toLowerCase()))
    );
  };

  // Render comment text with highlighted mentions
  const renderCommentText = (text) => {
    const mentionRegex = /@([\w.-]+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      
      // Find user by username to get their full name
      const username = match[1];
      const user = allUsers.find(u => u.username === username);
      const displayName = (user && (user.firstName || user.lastName)) 
        ? `@${user.firstName || ''} ${user.lastName || ''}`.trim()
        : `@${username}`;
      
      // Add mention with styling - now includes @ in the highlight
      parts.push(
        <span key={match.index} className="mention-highlight" title={`Username: ${username}`}>
          {displayName}
        </span>
      );
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  // Extract mentioned usernames from text
  const extractMentionedUsers = (text) => {
    const mentionRegex = /@([\w.-]+)/g;
    const mentions = [];
    let match;
    while ((match = mentionRegex.exec(text)) !== null) {
      mentions.push(match[1]);
    }
    return [...new Set(mentions)]; // Remove duplicates
  };

  const renderDiff = (oldText = '', newText = '') => {
    // Strip HTML tags for comparison and display
    const stripHtml = (html) => {
      if (!html) return '';
      const div = document.createElement('div');
      div.innerHTML = html;
      return div.textContent || div.innerText || '';
    };

    const oldClean = stripHtml(oldText);
    const newClean = stripHtml(newText);
    
    // If texts are the same, just show it normally
    if (oldClean === newClean) {
      return (
        <div className="diff-content no-change">
          {newClean || <span className="empty-text">Empty</span>}
        </div>
      );
    }
    
    // Simple diff algorithm for text - split by words
    const oldWords = (oldClean || '').split(/\s+/).filter(w => w);
    const newWords = (newClean || '').split(/\s+/).filter(w => w);
    
    // If one is empty, show full add/delete
    if (oldWords.length === 0 && newWords.length > 0) {
      return (
        <div className="diff-content">
          <span className="diff-added">{newClean}</span>
        </div>
      );
    }
    
    if (newWords.length === 0 && oldWords.length > 0) {
      return (
        <div className="diff-content">
          <span className="diff-deleted">{oldClean}</span>
        </div>
      );
    }
    
    const result = [];
    let i = 0, j = 0;
    
    while (i < oldWords.length || j < newWords.length) {
      if (i < oldWords.length && j < newWords.length && oldWords[i] === newWords[j]) {
        result.push(<span key={`same-${i}-${j}`}>{oldWords[i]} </span>);
        i++;
        j++;
      } else {
        // Find next matching word
        let foundMatch = false;
        
        // Check if old word was deleted
        if (i < oldWords.length) {
          const nextMatch = newWords.indexOf(oldWords[i], j);
          if (nextMatch !== -1 && nextMatch - j < 5) {
            // Words were added
            while (j < nextMatch) {
              result.push(
                <span key={`add-${j}`} className="diff-added">
                  {newWords[j]}{' '}
                </span>
              );
              j++;
            }
            foundMatch = true;
          }
        }
        
        // Check if new word was added
        if (j < newWords.length && !foundMatch) {
          const nextMatch = oldWords.indexOf(newWords[j], i);
          if (nextMatch !== -1 && nextMatch - i < 5) {
            // Words were deleted
            while (i < nextMatch) {
              result.push(
                <span key={`del-${i}`} className="diff-deleted">
                  {oldWords[i]}{' '}
                </span>
              );
              i++;
            }
            foundMatch = true;
          }
        }
        
        if (!foundMatch) {
          // Show as replacement
          if (i < oldWords.length) {
            result.push(
              <span key={`del-${i}`} className="diff-deleted">
                {oldWords[i]}{' '}
              </span>
            );
            i++;
          }
          if (j < newWords.length) {
            result.push(
              <span key={`add-${j}`} className="diff-added">
                {newWords[j]}{' '}
              </span>
            );
            j++;
          }
        }
      }
    }
    
    return <div className="diff-content">{result}</div>;
  };

  const renderInitialContent = (text) => {
    // Strip HTML tags for display
    const stripHtml = (html) => {
      if (!html) return '';
      const div = document.createElement('div');
      div.innerHTML = html;
      return div.textContent || div.innerText || '';
    };

    const cleanText = stripHtml(text);
    
    return (
      <div className="diff-content initial-content">
        {cleanText || <span className="empty-text">Empty</span>}
      </div>
    );
  };

  const getFieldsForCreativeType = (creativeType) => {
    const type = creativeType?.toLowerCase();

    switch (type) {
      case 'sms':
        return [
          { key: 'body', label: 'Body' }
        ];
      case 'popup':
        return [
          { key: 'headline', label: 'Headline' },
          { key: 'body', label: 'Body' },
          { key: 'bodyBtnName', label: 'Button Name' },
          { key: 'bodyBtnLink', label: 'Button Link' }
        ];
      case 'push-notification':
      case 'push notification':
        return [
          { key: 'headline', label: 'Headline' },
          { key: 'body', label: 'Body' },
          { key: 'bodyBtnName', label: 'Button Name' },
          { key: 'bodyBtnLink', label: 'Button Link' }
        ];
      case 'email':
        return [
          { key: 'subjectLine1', label: 'Subject Line 1' },
          { key: 'subjectLine2', label: 'Subject Line 2' },
          { key: 'subjectLine3', label: 'Subject Line 3' },
          { key: 'preHeader', label: 'Pre-Header' },
          { key: 'body', label: 'Body' },
          { key: 'additionalText', label: 'Additional Text' },
          { key: 'additionalTextUnderBTN', label: 'Additional Text Under BTN' },
          { key: 'bodyBtnName', label: 'Button Name' },
          { key: 'bodyBtnLink', label: 'Button Link' }
        ];
      default:
        return [
          { key: 'body', label: 'Body' },
          { key: 'headline', label: 'Headline' },
          { key: 'bodyBtnName', label: 'Button Name' },
          { key: 'bodyBtnLink', label: 'Button Link' }
        ];
    }
  };

  const getPreviousVersion = (currentVersionNumber) => {
    return versions.find(v => v.versionNumber === currentVersionNumber - 1);
  };

  const hasChanges = (currentVersion, previousVersion) => {
    if (!previousVersion) return true; // First version always has "changes"
    
    // Check if any field is different
    if (currentVersion.title !== previousVersion.title) return true;
    
    // Check langs
    for (let i = 0; i < (currentVersion.langs || []).length; i++) {
      const currLang = currentVersion.langs[i];
      const prevLang = previousVersion.langs?.[i];
      
      if (!prevLang) return true;
      
      const fields = ['headline', 'subjectLine1', 'subjectLine2', 'subjectLine3', 
                     'preHeader', 'body', 'additionalText', 'additionalTextUnderBTN',
                     'bodyBtnName', 'bodyBtnLink'];
      
      for (const field of fields) {
        if (currLang[field] !== prevLang[field]) return true;
      }
    }
    
    return false;
  };

  const getLanguageStatus = (currentLangs, originalLangs, previousLangs, langIndex) => {
    if (!originalLangs) return 'initial'; // Original version
    
    const currentLang = currentLangs[langIndex];
    const currentLangName = currentLang?.languageName;
    
    // Check if this language existed in the ORIGINAL version (v1)
    const existedInOriginal = originalLangs.some(ol => ol.languageName === currentLangName);
    
    // Check if this language existed in PREVIOUS version
    const existedInPrevious = previousLangs?.some(pl => pl.languageName === currentLangName);
    
    // If exists in previous but NOT in original → it was added before, so still show as added (green)
    if (!existedInOriginal) {
      return 'added'; // New language added after original - GREEN
    }
    
    // If exists in original and current, it's unchanged (blue)
    return 'unchanged'; // Existing language from original - BLUE
  };

  const getRemovedLanguages = (currentLangs, originalLangs) => {
    if (!originalLangs) return [];
    
    const currentLangNames = currentLangs.map(l => l.languageName);
    // Return languages from original that are missing in current version - RED
    return originalLangs.filter(ol => !currentLangNames.includes(ol.languageName));
  };

  const getRemovedLanguagesFromPrevious = (currentLangs, previousLangs) => {
    // Languages that existed in PREVIOUS version but are MISSING in current version
    // This shows what was removed compared to the last version - RED
    if (!previousLangs) return [];
    
    const currentLangNames = currentLangs.map(l => l.languageName);
    return previousLangs.filter(pl => !currentLangNames.includes(pl.languageName));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('bg-BG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="version-history-modal">
        <div className="version-history-content">
          <div className="loading">Loading versions...</div>
        </div>
      </div>
    );
  }

  if (!canViewHistory) {
    return (
      <div className="version-history-modal">
        <div className="version-history-content">
          <div className="version-history-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 style={{ margin: 0 }}>Version History</h2>
            <button className="close-history-btn" onClick={onClose} title="Close" style={{ fontSize: '1.7rem', background: 'none', border: 'none', cursor: 'pointer', color: '#888', marginLeft: '16px', lineHeight: '1' }}>×</button>
          </div>
          <div className="no-permission-message" style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
            <p>You don't have permission to view version history.</p>
            <p style={{ fontSize: '0.9em', marginTop: '10px' }}>Contact your administrator for access.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="version-history-modal">
      <div className="version-history-content">
        <div className="version-history-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0 }}>Version History - Timeline</h2>
          <button className="close-history-btn" onClick={onClose} title="Close" style={{ fontSize: '1.7rem', background: 'none', border: 'none', cursor: 'pointer', color: '#888', marginLeft: '16px', lineHeight: '1' }}>×</button>
        </div>

        <div className="timeline-view">
          <div className="timeline-legend">
            <span className="legend-item">
              <span className="diff-deleted">Red</span> = Removed
            </span>
            <span className="legend-item">
              <span className="diff-added">Green</span> = Added
            </span>
            <span className="legend-note">Click on any version to expand/collapse changes</span>
          </div>

          <div className="timeline-container">
            {/* Version Timeline */}
            {versions.map((version, index) => {
              const previousVersion = getPreviousVersion(version.versionNumber);
              const originalVersion = versions.find(v => v.versionNumber === 1); // Get original version (v1)
              const isExpanded = expandedVersions.has(version.versionNumber);
              const hasAnyChanges = hasChanges(version, previousVersion);
              const isOriginal = version.versionNumber === 1;

              return (
                <div 
                  key={version._id} 
                  id={`version-${version._id}`}
                  className={`timeline-item ${isExpanded ? 'expanded' : ''}`}
                >
                  {/* Timeline connector */}
                  {index !== versions.length - 1 && <div className="timeline-line"></div>}
                  
                  {/* Version header */}
                  <div 
                    className="timeline-version-header"
                  >
                    <div className="timeline-marker">
                      <div className="timeline-dot"></div>
                      <div className={`version-badge ${isOriginal ? 'initial-version-badge' : ''}`}>
                        {isOriginal ? 'Original' : `v${version.versionNumber - 1}`}
                      </div>
                    </div>
                    
                    <div className="timeline-info">
                      <div className="timeline-title">{version.title}</div>
                      <div className="timeline-meta">
                        <span className="timeline-date">{formatDate(version.createdAt)}</span>
                        <span className="timeline-user">By: {version.editedBy}</span>
                        <div className="timeline-meta-actions">
                          {hasAnyChanges && (
                            <button 
                              className="view-changes-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleVersion(version.versionNumber);
                              }}
                            >
                              <FiEye size={16} /> {previousVersion ? 'View changes' : 'View content'}
                            </button>
                          )}
                          <button 
                            className="view-comments-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleComments(version._id);
                            }}
                          >
                            <FiMessageSquare size={16} /> Comments ({(comments[version._id] || []).length})
                          </button>
                        </div>
                      </div>
                      {version.changesSummary && (
                        <div className="timeline-summary">{version.changesSummary}</div>
                      )}
                    </div>

                    {canRestoreHistory && (
                      <div className="timeline-actions">
                        <button 
                          className="secondary-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRestore(version.versionNumber);
                          }}
                        >
                          Restore
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Expanded diff content */}
                  {isExpanded && (
                    <div className="timeline-diff-content">
                      {!previousVersion ? (
                        // Show initial content for first version
                        <div className="diff-sections">
                          <div className="initial-version-badge">
                            <span>✨ Creative Created</span>
                          </div>

                          {/* Language tabs */}
                          {version.langs && version.langs.length > 0 && (
                            <div className="language-tabs-section">
                              <div className="language-tabs">
                                {version.langs.map((lang, langIndex) => (
                                  <button
                                    key={langIndex}
                                    className={`language-tab initial ${
                                      selectedLanguages[version.versionNumber] === langIndex ? 'active' : ''
                                    }`}
                                    onClick={() => setSelectedLanguages(prev => ({
                                      ...prev,
                                      [version.versionNumber]: langIndex
                                    }))}
                                  >
                                    {getLangLabel(lang.languageName)}
                                  </button>
                                ))}
                              </div>

                              {/* Show content for selected language */}
                              {(() => {
                                const selectedLangIndex = selectedLanguages[version.versionNumber] || 0;
                                const lang = version.langs[selectedLangIndex];
                                if (!lang) return null;

                                const fields = getFieldsForCreativeType(version.creativeType);
                                const contentFields = [];

                                fields.forEach(field => {
                                  const value = lang[field.key];
                                  if (value && value.trim && value.trim() !== '') {
                                    contentFields.push(
                                      <div key={field.key} className="diff-field">
                                        <label>{field.label}:</label>
                                        {renderInitialContent(value)}
                                      </div>
                                    );
                                  }
                                });

                                return (
                                  <div className="language-content">
                                    {contentFields}
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      ) : (
                        // Show diff for subsequent versions
                        <div className="diff-sections">
                          {/* Title changes */}
                          {version.title !== previousVersion.title && (
                            <div className="diff-section">
                              <h4>Title</h4>
                              {renderDiff(previousVersion.title, version.title)}
                            </div>
                          )}

                          {/* Language tabs with status */}
                          {version.langs && version.langs.length > 0 && (
                            <div className="language-tabs-section">
                              <div className="language-tabs">
                                {/* Current languages */}
                                {version.langs.map((lang, langIndex) => {
                                  const status = getLanguageStatus(version.langs, originalVersion?.langs, previousVersion?.langs, langIndex);
                                  return (
                                    <button
                                      key={langIndex}
                                      className={`language-tab ${status} ${
                                        selectedLanguages[version.versionNumber] === langIndex ? 'active' : ''
                                      }`}
                                      onClick={() => setSelectedLanguages(prev => ({
                                        ...prev,
                                        [version.versionNumber]: langIndex
                                      }))}
                                    >
                                      {getLangLabel(lang.languageName)}
                                      {status === 'added' && <span className="lang-badge">+</span>}
                                    </button>
                                  );
                                })}

                                {/* Removed languages - compare with previous version to show what's missing */}
                                {previousVersion && getRemovedLanguagesFromPrevious(version.langs, previousVersion.langs)
                                  .map((removedLang, idx) => (
                                    <button
                                      key={`removed-${idx}`}
                                      className="language-tab removed"
                                      disabled
                                    >
                                      {getLangLabel(removedLang.languageName)}
                                      <span className="lang-badge">-</span>
                                    </button>
                                  ))
                                }
                              </div>

                              {/* Show content for selected language */}
                              {(() => {
                                const selectedLangIndex = selectedLanguages[version.versionNumber] || 0;
                                const lang = version.langs[selectedLangIndex];
                                if (!lang) return null;

                                const prevLang = previousVersion.langs.find(pl => pl.languageName === lang.languageName);
                                const fields = getFieldsForCreativeType(version.creativeType);
                                const changedFields = [];

                                fields.forEach(field => {
                                  const currentValue = lang[field.key];
                                  const prevValue = prevLang?.[field.key];
                                  
                                  const normalizedCurrent = (currentValue && typeof currentValue === 'string' && currentValue.trim()) ? currentValue : '';
                                  const normalizedPrev = (prevValue && typeof prevValue === 'string' && prevValue.trim()) ? prevValue : '';
                                  
                                  if (normalizedCurrent !== normalizedPrev) {
                                    changedFields.push(
                                      <div key={field.key} className="diff-field">
                                        <label>{field.label}:</label>
                                        {renderDiff(normalizedPrev, normalizedCurrent)}
                                      </div>
                                    );
                                  }
                                });

                                return (
                                  <div className="language-content">
                                    {changedFields.length > 0 ? changedFields : (
                                      <div className="no-changes-notice">
                                        <p>No changes in this language</p>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          )}

                          {!hasAnyChanges && (
                            <div className="no-changes-notice">
                              <p>No changes detected in this version.</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Comments section - separate from diff content */}
                  {expandedComments.has(version._id) && (
                    <div className="version-comments-section">
                      <h4 className="comments-header">Comments ({(comments[version._id] || []).length})</h4>
                      
                      {/* Display existing comments */}
                      <div className="comments-list">
                        {(comments[version._id] || []).map((comment, idx) => (
                          <div key={idx} className="comment-item">
                            <div className="comment-header">
                              <span className="comment-author">{comment.userName}</span>
                              <span className="comment-date">{formatDate(comment.createdAt)}</span>
                              {currentUser && (currentUser.username === comment.userId || currentUser.isAdmin) && (
                                <div className="comment-actions">
                                  <button
                                    className="action-icon-btn btn btn-outline-secondary btn-sm"
                                    onClick={() => setEditingComment({ versionId: version._id, commentId: comment._id, text: comment.text })}
                                    title="Edit"
                                  >
                                    <FiEdit size={14} />
                                  </button>
                                  <button
                                    className="action-icon-btn btn btn-outline-danger btn-sm"
                                    onClick={() => handleDeleteComment(version._id, comment._id)}
                                    disabled={deletingComment === comment._id}
                                    title="Delete"
                                  >
                                    <FiTrash2 size={14} />
                                  </button>
                                </div>
                              )}
                            </div>
                            {editingComment?.commentId === comment._id ? (
                              <div className="edit-comment-form">
                                <textarea
                                  className="comment-input"
                                  value={editingComment.text}
                                  onChange={(e) => handleTextareaChange(e, version._id, true)}
                                  rows={3}
                                />
                                <div className="edit-comment-actions">
                                  <button
                                    className="save-comment-btn"
                                    onClick={() => handleEditComment(version._id, comment._id)}
                                  >
                                    Save
                                  </button>
                                  <button
                                    className="cancel-comment-btn"
                                    onClick={() => setEditingComment(null)}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="comment-text">{renderCommentText(comment.text)}</div>
                            )}
                          </div>
                        ))}
                        {(comments[version._id] || []).length === 0 && (
                          <div className="no-comments">No comments yet</div>
                        )}
                      </div>

                      {/* Add new comment */}
                      <div className="add-comment-form">
                        <textarea
                          className="comment-input"
                          placeholder="Add a comment..."
                          value={newComment[version._id] || ''}
                          onChange={(e) => handleTextareaChange(e, version._id)}
                          rows={3}
                        />
                        <button
                          className="add-comment-btn"
                          onClick={() => handleAddComment(version._id)}
                          disabled={!newComment[version._id]?.trim() || submittingComment[version._id]}
                        >
                          {submittingComment[version._id] ? 'Adding...' : 'Add Comment'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {versions.length === 0 && !loading && (
            <div className="no-versions-notice">
              <p>No version history available yet. Original version will be created on first edit.</p>
            </div>
          )}

          {versions.length === 1 && versions[0].versionNumber === 1 && !loading && (
            <div className="no-versions-notice">
              <p>No edits yet. This creative is still in its original state.</p>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay version-history-modal" onClick={cancelRestore}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Restore</h3>
            <p>Are you sure you want to restore to {versionToRestore === 1 ? 'Original' : `Version ${versionToRestore - 1}`}?</p>
            <p className="modal-note">This will create a backup of the current state.</p>
            <div className="modal-actions">
              <button className="confirm-button" onClick={confirmRestore}>
                Yes, Restore
              </button>
              <button className="cancel-button" onClick={cancelRestore}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mention Dropdown */}
      {showMentionDropdown && (
        <div 
          className="mention-dropdown"
          style={{
            position: 'fixed',
            top: `${mentionPosition.top}px`,
            left: `${mentionPosition.left}px`,
            zIndex: 10000
          }}
        >
          {getFilteredUsers().length > 0 ? (
            getFilteredUsers().map(user => {
              const displayName = (user.firstName || user.lastName) 
                ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                : user.username;
              const roleName = user.role?.name || 'User';
              return (
                <div
                  key={user._id}
                  className="mention-item"
                  onClick={() => handleMentionSelect(user)}
                >
                  <div className="mention-user-info">
                    <strong>{displayName}</strong>
                    <span className="mention-role">{roleName}</span>
                  </div>
                  {(user.firstName || user.lastName) && (
                    <span className="mention-username">@{user.username}</span>
                  )}
                </div>
              );
            })
          ) : (
            <div className="mention-item no-results">No users found</div>
          )}
        </div>
      )}

      {/* Notification Modal */}
      {showNotification && (
        <div className={`notification-toast ${notification.type}`}>
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default VersionHistory;
